import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Button, Spin } from "antd";
import ForceGraph2D from "react-force-graph-2d";
import { forceX, forceY, forceCollide } from "d3-force-3d";
import {
  Box,
  Popover
} from "@mui/material";
import {colorNode, highlightNode, LabelV2Node, LabelSelectedNode as LabelLinkedNodes} from "./GraphViewerConfig";
import './Graph.css';
import './DetailBox.css';
import DetailBox from "./DetailBox";
import EdgeInfoDrawer from "./EdgeInfoDrawer";
import NodeConnectionsDrawer from "./NodeConnectionsDrawer";
import { connectedGraph } from "../utils/connectedGraph";
import { nodeDegrees, degreeRadius } from "../utils/nodeDegree";
import { getLinks, getNodes } from "../utils/filerHandler";
import { edgeEndpoints, shareSelectionKey } from "../utils/graphShareState";
import SearchBar from "./SearchBar";

const NODE_R = 8; // Default node radius
const DETAIL_POPOVER_MARGIN = 16;
const DETAIL_POPOVER_DEFAULT_POSITION = { top: 500, left: 800 };
const DETAIL_POPOVER_FALLBACK_SIZE = { width: 420, height: 520 };

/**
 * GraphViewer component to display the graph.
 * @param {Object} data - The complete graph data containing all nodes and links for a single graph.
 * @param {Object} details - The meta data for the graph, including communities, text units, etc.
 * @param {Object} selectedGraph - The currently selected sub-graph.
 * @param {Function} selectCommunity - Function to select a community
 * @returns {JSX.Element} The rendered GraphViewer component.
 */
function GraphViewer ({
  provenance,
  data,
  graphId,
  onResetGraph,
  onAttributeFilter,
  onDrawerOpenChange,
  categoryFilter = null,
  isV2 = false,
  statisticalView = false,
  searchNodesOnly = false,
  shareSelection = {},
  onShareSelectionChange,
  hideIsolatedNodes = false,
  colorByContextSubtype = false,
  details, 
  selectedGraph,
  selectCommunity,
}){
  // States for basic graph interaction
  // Nodes and links have 4 states: default (no interaction), highlighted(with hover), labeled (with left click), and focus (with right click).
  // For highlighted/labeled nodes and links, their neighbors and links are also highlighted/labeled.
  const [selectedNode, setSelectedNode] = useState(null); // The node selected by right click or search
  const [selectedRelationship, setSelectedRelationship] = useState(null); // The relationship selected by right click or search
  const [hoverNodes, setHoverNodes] = useState(new Set()); // Nodes that are highlighted when hovered over
  const [hoverLinks, setHoverLinks] = useState(new Set()); // Links that are highlighted when hovered over
  const [linkedNodes, setLinkedNodes] = useState([]); // Labeled nodes that are linked to the selected node / highlighted by left click
  const [linkedRelationships, setLinkedRelationships] = useState([]); // Labeled relationships that are linked to the selected node / highlighted by left click

  //States for graph data
  const graphRef = useRef();
  const autoFitPending = useRef(false);
  const [graphData, setGraphData] = useState(data); // The graph data to be displayed in the viewer. Initially set to the complete graph data.
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const appliedShareSelection = useRef('');
  const requestedSelectionKey = shareSelectionKey(shareSelection);
  const baseVisibleData = useMemo(() => {
    if (!hideIsolatedNodes) return graphData;
    const endpoint = value => typeof value === "object" && value !== null ? value.id : value;
    const ids = new Set(graphData.nodes.map(node => node.id));
    const links = graphData.links.filter(link => ids.has(endpoint(link.source)) && ids.has(endpoint(link.target)));
    const connected = new Set(links.flatMap(link => [endpoint(link.source), endpoint(link.target)]));
    return { nodes: graphData.nodes.filter(node => connected.has(node.id)), links };
  }, [graphData, hideIsolatedNodes]);
  const focusedComponent = useMemo(() => focusedNodeId ? connectedGraph(baseVisibleData, focusedNodeId) : null, [baseVisibleData, focusedNodeId]);
  const visibleGraphData = focusedComponent || baseVisibleData;
  // Keep sizes stable when zooming into a component; filters determine degree.
  const degrees = useMemo(() => nodeDegrees(baseVisibleData), [baseVisibleData]);
  const nodeRadius = useCallback(node => isV2 ? degreeRadius(degrees.get(node.id) || 0) : NODE_R, [isV2, degrees]);
  const focusedNode = focusedComponent?.nodes.find(node => node.id === focusedNodeId);
  const [settledData, setSettledData] = useState(null);
  const layoutLoading = isV2 && visibleGraphData.nodes.length > 0 && settledData !== visibleGraphData;
  const initialGraphData = useRef(data); 

  const [open, setOpen] = useState(false); // State to control the visibility of the detail box
  const [detailAnchorPosition, setDetailAnchorPosition] = useState(DETAIL_POPOVER_DEFAULT_POSITION);
  const [dimensions, setdimensions] = useState({width: 600, height: 500}); // Dimensions of the graph container, initially set to 600x500
  const containerRef = useRef(null);
  const hasInlineControls = categoryFilter !== null; // Ref to the graph container
  const detailBoxRef = useRef(null);
  useEffect(() => { onDrawerOpenChange?.(isV2 && open && Boolean(selectedNode || selectedRelationship)); }, [isV2, open, selectedNode, selectedRelationship, onDrawerOpenChange]);
  
  // Effect to resize the graph to the size of its container
  useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const nextWidth = Math.max(0, Math.floor(rect.width));
      const nextHeight = Math.max(0, Math.floor(rect.height));

      if (nextWidth > 0 && nextHeight > 0) {
        setdimensions((prev) => {
          if (prev.width === nextWidth && prev.height === nextHeight) {
            return prev;
          }
          return { width: nextWidth, height: nextHeight };
        });
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDimensions);
    };
  }, [hasInlineControls]);

  // Pull disconnected components toward the centre without adding graph edges.
  useEffect(() => {
    if (!isV2 || !graphRef.current) return;
    const graph = graphRef.current;
    graph.d3Force('compact-x', forceX(0).strength(0.045));
    graph.d3Force('compact-y', forceY(0).strength(0.045));
    graph.d3Force('collision', forceCollide(node => nodeRadius(node) * 1.15 + 2).iterations(2));
    graph.d3Force('charge').strength(-35).distanceMax(250);
    graph.d3Force('link').distance(35);
    autoFitPending.current = true;
    graph.d3ReheatSimulation();
    return () => {
      graph.d3Force('compact-x', null);
      graph.d3Force('compact-y', null);
      graph.d3Force('collision', null);
      graph.d3Force('charge').strength(-30).distanceMax(Infinity);
      graph.d3Force('link').distance(30);
    };
  }, [isV2, visibleGraphData, nodeRadius]);

  const fitWhenSettled = useCallback(() => {
    if (!graphRef.current) return;
    if (autoFitPending.current) {
      autoFitPending.current = false;
      if (visibleGraphData.nodes.length) {
        if (focusedNodeId && visibleGraphData.nodes.length === 1) graphRef.current.zoom(4, 0);
        else graphRef.current.zoomToFit(0, 32);
        const bounds = graphRef.current.getGraphBbox();
        if (bounds) graphRef.current.centerAt((bounds.x[0] + bounds.x[1]) / 2, (bounds.y[0] + bounds.y[1]) / 2, 0);
      }
    }
    // Reveal only after the final layout has been fitted to the canvas.
    setSettledData(visibleGraphData);
  }, [visibleGraphData, focusedNodeId]);

  useEffect(() => {
    if (isV2 && graphRef.current && visibleGraphData.nodes.length) {
      if (focusedNodeId && visibleGraphData.nodes.length === 1) {
        graphRef.current.zoom(4, 300);
        graphRef.current.centerAt(visibleGraphData.nodes[0].x, visibleGraphData.nodes[0].y, 300);
      } else graphRef.current.zoomToFit(300, 32);
    }
  }, [isV2, dimensions.width, dimensions.height, visibleGraphData.nodes, focusedNodeId]);

  // Effect to update graph data when the data prop changes (i.e. changing to another graph)
  useEffect(() => {
    appliedShareSelection.current = '';
    setFocusedNodeId(null);
    setGraphData(data);
    initialGraphData.current = data;
  }, [data]);

  // Effect to update graph data when the selectedGraph prop changes
  useEffect(() => {
    if (!selectedGraph || !selectedGraph.nodes || !selectedGraph.links) {
      console.warn("Selected graph is empty or invalid.");
      return;
    }
    appliedShareSelection.current = '';
    if (selectedGraph.nodes.length === 0 && selectedGraph.links.length === 0) {
      setGraphData(data);
    } else {
      const nodes = getNodes(selectedGraph.nodes, data.nodes);
      const links = getLinks(selectedGraph.links, data.links);
      setGraphData({
        nodes: nodes,
        links: links
      });
    }
    setFocusedNodeId(null);
    setHoverLinks(new Set());
    setHoverNodes(new Set());
    setSelectedNode(null);
    setSelectedRelationship(null);
    setLinkedNodes([]);
    setLinkedRelationships([]);
    setOpen(false);
  }, [selectedGraph, data]);

  // Handle node hover and link hover events to highlight their neighbors
  const handleNodeHover = useCallback((node) => {
    const newHighlightNodes = new Set(); //nodes
    const newHighlightLinks = new Set(); //links

    if (node) {
      newHighlightNodes.add(node);
      node.neighbors?.forEach((neighbor) => newHighlightNodes.add(neighbor));
      node.links?.forEach((link) => newHighlightLinks.add(link));
    }
    setHoverNodes(newHighlightNodes);
    setHoverLinks(newHighlightLinks);
    // setHoverNode(node);
  }, []);

  const handleLinkHover = useCallback((link) => {
    const newHighlightNodes = new Set();
    const newHighlightLinks = new Set();

    if (link) {
      newHighlightLinks.add(link);
      if (typeof link.source !== "string") newHighlightNodes.add(link.source);
      if (typeof link.target !== "string") newHighlightNodes.add(link.target);
    }

    setHoverNodes(newHighlightNodes);
    setHoverLinks(newHighlightLinks);
    // setHoverLink(link);
  }, []);

  // Effect to handle node and link left clicks (label)
  const handleNodeClick = (node) => {
    if (isV2) {
      setFocusedNodeId(node.id);
      setSelectedNode(node);
      setSelectedRelationship(null);
      setLinkedNodes([node]);
      setLinkedRelationships([]);
      setOpen(true);
      const selection = { focusNodeId: node.id, nodeId: node.id, edge: null };
      appliedShareSelection.current = shareSelectionKey(selection);
      onShareSelectionChange?.(selection);
      return;
    }
    setLinkedNodes([node, ...(node.neighbors || [])]);
    setLinkedRelationships(node.links || []);
  };
  const handleLinkClick = (link) => {
    const linkedNodes = [];
    if (typeof link.source !== "string") {
      linkedNodes.push(link.source);
    }
    if (typeof link.target !== "string") {
      linkedNodes.push(link.target);
    }
    setLinkedNodes(Array.from(linkedNodes));
    setLinkedRelationships([link]);
    if (isV2) {
      if (!focusedNodeId) autoFitPending.current = false;
      setSelectedRelationship(link);
      setSelectedNode(null);
      setOpen(true);
      const selection = { focusNodeId: focusedNodeId, nodeId: null, edge: edgeEndpoints(link) };
      appliedShareSelection.current = shareSelectionKey(selection);
      onShareSelectionChange?.(selection);
    }
    }

  const updateDetailAnchorPosition = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const boxRect = detailBoxRef.current?.getBoundingClientRect();
    const boxWidth = boxRect?.width || DETAIL_POPOVER_FALLBACK_SIZE.width;
    const boxHeight = boxRect?.height || DETAIL_POPOVER_FALLBACK_SIZE.height;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const nextTop = Math.max(
      DETAIL_POPOVER_MARGIN,
      Math.min(
        DETAIL_POPOVER_DEFAULT_POSITION.top,
        viewportHeight - boxHeight - DETAIL_POPOVER_MARGIN
      )
    );
    const nextLeft = Math.max(
      DETAIL_POPOVER_MARGIN,
      Math.min(
        DETAIL_POPOVER_DEFAULT_POSITION.left,
        viewportWidth - boxWidth - DETAIL_POPOVER_MARGIN
      )
    );

    setDetailAnchorPosition((previousPosition) => {
      if (previousPosition.top === nextTop && previousPosition.left === nextLeft) {
        return previousPosition;
      }
      return { top: nextTop, left: nextLeft };
    });
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    updateDetailAnchorPosition();
    const resizeObserver = new ResizeObserver(updateDetailAnchorPosition);

    if (detailBoxRef.current) {
      resizeObserver.observe(detailBoxRef.current);
    }

    window.addEventListener("resize", updateDetailAnchorPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDetailAnchorPosition);
    };
  }, [open, selectedNode, selectedRelationship, updateDetailAnchorPosition]);

  // Functions that returns boolean to check states of node and link
  const isHighlightedNode = (node) => {
    return hoverNodes.has(node) || linkedNodes.includes(node);
  };
  const isLinkedNode = (node) => {
    return linkedNodes && (linkedNodes.includes(node) || selectedNode === node);
  };
  const isHighlightedLink = (link) => {
    return hoverLinks.has(link) || linkedRelationships.includes(link);
  };

  // Functions to focus on a node or link (right click)
  const focusNode = (node) => {
    autoFitPending.current = false;

    // Right click includes the effect of left click
    handleNodeClick(node);
    if (isV2) return;
    // Move canvas and zoom in on the node
    if (graphRef.current) {
        graphRef.current.centerAt(node.x, node.y, 1000);
        graphRef.current.zoom(4, 1000);
    }
    // Open the detail box
    setOpen(true);
    setSelectedNode(node);
    setSelectedRelationship(null);
  };

  const focusLink = (link) => {
    if (isV2) { handleLinkClick(link); return; }
    autoFitPending.current = false;

    handleLinkClick(link);
    setSelectedRelationship(link);
    setSelectedNode(null);
    if (graphRef.current) {
      const sourceNode = typeof link.source !== "string" ? link.source : null;
      const targetNode = typeof link.target !== "string" ? link.target : null;
      graphRef.current.centerAt(
        (sourceNode?.x + targetNode?.x) / 2,
        (sourceNode?.y + targetNode?.y) / 2,
        1000
      );
      graphRef.current.zoom(2, 1000);
    }
    setOpen(true);
  }

  // Restore a shared node/edge after graph data and filters have been applied.
  useEffect(() => {
    if (!isV2) return;
    const selectionKey = requestedSelectionKey;
    if (!selectionKey) {
      if (appliedShareSelection.current) {
        appliedShareSelection.current = '';
        setFocusedNodeId(null);
        setSelectedNode(null);
        setSelectedRelationship(null);
        setLinkedNodes([]);
        setLinkedRelationships([]);
        setOpen(false);
      }
      return;
    }
    if (appliedShareSelection.current === selectionKey) return;
    const sharedNode = shareSelection.nodeId
      ? baseVisibleData.nodes.find(node => node.id === shareSelection.nodeId)
      : null;
    if (sharedNode) {
      setFocusedNodeId(shareSelection.focusNodeId || sharedNode.id);
      setSelectedNode(sharedNode);
      setSelectedRelationship(null);
      setLinkedNodes([sharedNode]);
      setLinkedRelationships([]);
      setOpen(true);
      appliedShareSelection.current = selectionKey;
      return;
    }
    if (shareSelection.edge) {
      const desired = JSON.stringify([...shareSelection.edge].sort());
      const link = baseVisibleData.links.find(candidate => JSON.stringify(edgeEndpoints(candidate)) === desired);
      if (link) {
        const endpoints = [link.source, link.target].filter(value => typeof value === 'object');
        setFocusedNodeId(shareSelection.focusNodeId || null);
        setSelectedNode(null);
        setSelectedRelationship(link);
        setLinkedNodes(endpoints);
        setLinkedRelationships([link]);
        setOpen(true);
        appliedShareSelection.current = selectionKey;
        return;
      }
    }
    if (shareSelection.focusNodeId) {
      const focus = baseVisibleData.nodes.find(node => node.id === shareSelection.focusNodeId);
      if (focus) {
        setFocusedNodeId(focus.id);
        setSelectedNode(null);
        setSelectedRelationship(null);
        setOpen(false);
        appliedShareSelection.current = selectionKey;
      }
    }
  }, [isV2, requestedSelectionKey, shareSelection, baseVisibleData]);

  // Function to handle background click to reset the graph state
  const onBackgroundClick = () => {
    if (isV2) setOpen(false);
    setHoverNodes(new Set());
    setHoverLinks(new Set());
    setSelectedNode(null);
    setSelectedRelationship(null);
    setLinkedNodes([]);
    setLinkedRelationships([]);
    setOpen(false);
    const selection = { focusNodeId: focusedNodeId, nodeId: null, edge: null };
    appliedShareSelection.current = shareSelectionKey(selection);
    onShareSelectionChange?.(selection);
  }

  const resetGraph = () => {
    setFocusedNodeId(null);
    setGraphData(data);
    setHoverNodes(new Set()); setHoverLinks(new Set());
    setLinkedNodes([]); setLinkedRelationships([]);
    setSelectedNode(null); setSelectedRelationship(null); setOpen(false);
    appliedShareSelection.current = '';
    onShareSelectionChange?.({ focusNodeId: null, nodeId: null, edge: null });
    onResetGraph?.();
    autoFitPending.current = true;
    graphRef.current?.d3ReheatSimulation();
  };

  return (
    <Box className={`force-graph-container${categoryFilter ? ' force-graph-container-v2' : ''}`} ref={categoryFilter ? null : containerRef} sx={{ width: '100%', height: '100%' }}> 
    {/* Top level: display legend and search bar */}
      <div className={categoryFilter ? "graph-v2-controls" : undefined}>
      {categoryFilter && <div className="graph-category-row">{categoryFilter}</div>}
      <SearchBar
        nodesOnly={searchNodesOnly}
        inline={Boolean(categoryFilter)}  
        handleNodeSelect={focusNode}
        handleLinkSelect={focusLink}
        graphData={visibleGraphData}
        communities={details.community_reports}
        selectCommunity={selectCommunity}/>
      </div>
    {/* Popover for the detail box */}
      <Popover
        className="detail-popover"
        open={open && !isV2}
        onClose={() => setOpen(false)}
        anchorReference="anchorPosition"
        anchorPosition={detailAnchorPosition}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        marginThreshold={DETAIL_POPOVER_MARGIN}
      >
        <div ref={detailBoxRef}>
          <DetailBox node={selectedNode} link={selectedRelationship} details={details} focusNode={focusNode} focusLink={focusLink} />
        </div>
      </Popover>
      {isV2 && <EdgeInfoDrawer paperNames={provenance?.paper_names} paperSources={provenance?.paper_sources} open={open && Boolean(selectedRelationship)} onClose={() => setOpen(false)} link={selectedRelationship} graphId={graphId} onBack={focusedNode ? () => {
        setSelectedRelationship(null);
        setSelectedNode(focusedNode);
        setOpen(true);
        const selection = { focusNodeId: focusedNode.id, nodeId: focusedNode.id, edge: null };
        appliedShareSelection.current = shareSelectionKey(selection);
        onShareSelectionChange?.(selection);
      } : undefined} />}
      {isV2 && <NodeConnectionsDrawer provenance={provenance} statisticalView={statisticalView} open={open && Boolean(selectedNode) && !selectedRelationship} onClose={() => setOpen(false)} node={selectedNode} component={focusedComponent} onSelectEdge={handleLinkClick} colorByContextSubtype={colorByContextSubtype} onAttributeFilter={onAttributeFilter} />}
    {/* ForceGraph2D component to render the graph; Configuration to implement actions and manage the 4 states. */}
      <div className={categoryFilter ? "graph-v2-canvas" : undefined} ref={categoryFilter ? containerRef : null}
        aria-busy={isV2 ? layoutLoading : undefined}
        role={categoryFilter ? "img" : undefined}
        aria-label={categoryFilter ? `Graph with ${visibleGraphData.nodes.length} nodes and ${visibleGraphData.links.length} links` : undefined}>
      {isV2 && <Button className="graph-reset-button" onClick={resetGraph}>Reset graph</Button>}
      {layoutLoading && (
        <div className="graph-loading-overlay" role="status" aria-live="polite">
          <div className="graph-loading-content">
            <Spin size="large" />
            <span>Preparing network graph…</span>
          </div>
        </div>
      )}
      {isV2 && !visibleGraphData.nodes.length && (
        <div className="graph-loading-overlay" role="status">No nodes match the current filters.</div>
      )}
      <ForceGraph2D
        className="force-graph"
        ref={graphRef}
        width={dimensions.width}
        height={dimensions.height}
        graphData={visibleGraphData}
        cooldownTicks={isV2 ? 180 : Infinity}
        onEngineStop={isV2 ? fitWhenSettled : undefined}
        onNodeDrag={isV2 ? () => { autoFitPending.current = false; } : undefined}
        nodeRelSize={NODE_R}
        maxZoom={isV2 ? 4 : 1000}
        nodeVal={(node) => isV2
          ? Math.pow(nodeRadius(node) / NODE_R, 2)
          : isHighlightedNode(node) ? 2 : 1}
        nodeColor={(node) => isHighlightedNode(node) ? highlightNode(node, colorByContextSubtype) : colorNode(node, colorByContextSubtype)} //highlight node when hovered or labeled
        autoPauseRedraw={false}
        linkDirectionalParticles={(link) => isHighlightedLink(link) ? 3 : 0} // show particles to indicate directions on highlighted links
        linkCurvature={link => { const id = endpoint => typeof endpoint === 'object' ? endpoint.id : endpoint; return id(link.source) === id(link.target) ? 0.55 : 0; }}
        linkWidth={(link) => isHighlightedLink(link) ? 2 : 1} // highlight link when hovered or labeled
        onNodeHover={handleNodeHover} 
        onLinkHover={handleLinkHover}
        onNodeClick={handleNodeClick}
        onNodeRightClick={focusNode}
        onLinkClick={handleLinkClick}
        onLinkRightClick={focusLink}
        backgroundColor={"white"}
        onBackgroundClick={onBackgroundClick}
        // render a label for focused nodes/links
        nodeCanvasObjectMode={isV2 ? undefined : node => isLinkedNode(node) ? "after" : undefined}
        nodeCanvasObject={isV2 ? undefined : (node, ctx) => LabelLinkedNodes(node, ctx, colorByContextSubtype)}
        onRenderFramePost={isV2 ? (ctx, globalScale) => {
          visibleGraphData.nodes.filter(isLinkedNode).forEach(node => LabelV2Node(node, ctx, globalScale, nodeRadius(node), colorByContextSubtype));
        } : undefined}
      />

      </div>
    </Box>
  );
};

export default GraphViewer;
